import { db, withSyncWrite } from '../db/indexeddb'
import type { SyncConflict } from './types'
import type { SyncQueueItem } from '../db/models'
import type Dexie from 'dexie'

/**
 * Generic Pull Engine
 * Pulls remote entities from cloud into local DB using a descriptor-based approach.
 */

/** Configuration for pulling a specific entity type from cloud. */
export interface PullDescriptor<TRemote> {
  entityName: string
  settingsKey: string

  // Remote operations
  fetchRemote: (userId: string, since?: Date) => Promise<TRemote[]>
  fetchRemoteIds: (userId: string) => Promise<string[]>
  fetchRemoteByIds: (userId: string, ids: string[]) => Promise<TRemote[]>
  fetchDeletedRemote: (userId: string, since?: Date) => Promise<Array<{ id: string; local_id?: string | null; updated_at: string }>>

  // Schema & mapping
  safeParse: (raw: unknown) => { success: true; data: TRemote } | { success: false; error: { message: string } }
  getRemoteId: (remote: TRemote) => string
  getRemoteTimestamp: (remote: TRemote) => string
  getRemoteLocalId: (remote: TRemote) => string | null | undefined

  // Local DB operations
  table: Dexie.Table
  getAllLocal: () => Promise<Array<{ id: string; cloudId: string | null; synced: boolean; updatedAt: Date; deletedAt: Date | null; [key: string]: any }>>

  // Entity-specific create/update logic
  buildLocalInsert: (remote: TRemote, userId: string) => Promise<Record<string, any> | null>
  buildLocalUpdate: (remote: TRemote, existing: any) => Promise<Record<string, any> | null>

  // Optional: safety check to force full sync (e.g. zero local tasks)
  shouldForceFullSync?: (sinceDate: Date) => Promise<boolean>

  /**
   * Optional: nachdem eine Löschung aus der Cloud hier angekommen ist (Schritt 7). Eine
   * Aufgabe nimmt dann ihre Kommentare mit (#182) — hat ein Gerät einen Kommentar
   * geschrieben, während ein anderes die Aufgabe löschte, kannte dessen Kaskade ihn nicht.
   */
  afterRemoteDelete?: (localId: string) => Promise<void>

  /**
   * Optional: Reihenfolge, in der die geholten Zeilen angelegt werden. Aufgaben
   * legen Elternteile vor ihre Kinder, damit ein Kind im selben Durchgang sein
   * Elternteil findet (T14 #179).
   */
  orderRemote?: (records: TRemote[]) => TRemote[]
}

export interface PullResult {
  created: number
  updated: number
  deleted: number
  conflicts: SyncConflict[]
  errors: string[]
}

/**
 * Cloud ids that were fetched during ID-set reconciliation but couldn't be
 * materialised locally (parent not yet synced, or a permanently-orphaned cloud
 * record), keyed by entity name. Session-scoped — a reload / full sync re-detects
 * them. Counting these as "accounted for" lets the count-gated reconciliation
 * converge; without it an orphan keeps `cloudCount > localCount` forever, so
 * `fetchRemoteIds` + `fetchRemoteByIds` re-run on every single sync (observed in
 * the list window as a "full sync" every few seconds).
 */
const reconcileSkipCache = new Map<string, Set<string>>()

function rememberSkippedCloudId(entityName: string, cloudId: string): void {
  let set = reconcileSkipCache.get(entityName)
  if (!set) {
    set = new Set()
    reconcileSkipCache.set(entityName, set)
  }
  set.add(cloudId)
}

function forgetSkippedCloudId(entityName: string, cloudId: string): void {
  reconcileSkipCache.get(entityName)?.delete(cloudId)
}

/**
 * Zählstand und jüngster Zeitstempel einer Tabelle in der Cloud, aus der
 * **Vorprüfung** (`get_sync_state`). Strukturgleich mit `CloudEntityState` aus
 * `lib/supabase/queries` — hier eigen deklariert, damit `lib/sync` nicht am
 * Supabase-Modul hängt.
 */
export interface CloudEntityState {
  count: number
  /** `max(updated_at)` über ALLE Zeilen, auch gelöschte. `null` = Tabelle leer. */
  maxUpdatedAt: string | null
}

/**
 * Der Zählstand, bei dem die **Abstimmung** zuletzt durchgelaufen ist.
 *
 * Ohne diesen Merker lief sie bei jedem Sync erneut, sobald die Zählung einmal
 * auseinanderging — und fand dabei nichts. Zwei Wege dahin, beide am 20.09.2026
 * gemessen: eine lokal soft-gelöschte Zeile, die in der Cloud noch lebt (sie
 * fällt aus `localCloudIdCount`, aber nicht aus `missingIds`), und eine
 * Cloud-Zeile, deren `local_id` lokal schon einer anderen `cloudId` gehört.
 *
 * Statt jede Ursache einzeln zu jagen: Fand ein Durchgang nichts, ist die Sache
 * bei DIESEM Zählstand erledigt. Ändert sich die Zahl, wird neu abgestimmt.
 */
const lastReconciledCount = new Map<string, number>()

/**
 * Welche Tabellen in dieser Sitzung schon einen vollen Lauf hinter sich haben.
 *
 * Der erste Lauf nach App-Start geht bewusst an der Vorprüfung vorbei: Die volle
 * Runde war bisher nebenbei eine Reparatur, und ein Gerät mit kaputtem
 * Wasserstand (Rücksicherung, Absturz mitten im Lauf) würde sonst nie wieder
 * nachsehen. Siehe ADR 0027.
 */
const fullRunDone = new Set<string>()

/** Test-only: reset the session-scoped pull accounting (skips, counts, first run). */
export function __resetPullSession(): void {
  reconcileSkipCache.clear()
  lastReconciledCount.clear()
  fullRunDone.clear()
}

/** @deprecated Heißt `__resetPullSession()` — Name aus der Zeit vor der Vorprüfung. */
export function __resetReconcileSkipCache(): void {
  __resetPullSession()
}

/**
 * Die **Vorprüfung**: Darf dieser Lauf die Tabelle komplett überspringen?
 *
 * Überspringen heißt: kein `fetchRemote`, kein `fetchDeletedRemote`, keine
 * Abstimmung — und auch kein `getAllLocal()`, also nicht einmal ein Lesen der
 * lokalen Tabelle.
 */
function canSkipPull(
  entityName: string,
  state: CloudEntityState,
  sinceDate: Date | undefined,
): boolean {
  if (state.maxUpdatedAt === null) {
    // Kein einziger Zeitstempel heißt: keine Zeile, auch keine gelöschte. Weder
    // Änderungen noch Löschungen noch die Abstimmung können hier etwas finden.
    // Das ist der Dauerläufer aus der Messung: `habits`, `habit_logs` und
    // `user_preferences` zogen bei JEDEM Lauf die ganze (leere) Tabelle, weil
    // `shouldForceFullSync` bei leerer lokaler Tabelle den Wasserstand verwirft.
    // (`count > 0` ohne Zeitstempel kann es nicht geben — wenn doch, lieber holen.)
    return state.count === 0
  }

  if (!sinceDate) return false

  // Die Abstimmung liefe ohnehin gleich an → nicht überspringen.
  if (lastReconciledCount.get(entityName) !== state.count) return false

  /*
    Als Datum vergleichen, nicht als Zeichenkette: Postgres liefert
    `2026-09-20T11:22:32.123456+00:00`, im Dexie steht `…T11:22:32.123Z`.
    Zeichenweise wäre das nie gleich.
  */
  return new Date(state.maxUpdatedAt).getTime() <= sinceDate.getTime()
}


/** Ob die Werte aus der Cloud etwas an der lokalen Zeile ändern würden — Datumswerte nach Zeitpunkt verglichen. */
function differsFromLocal(updates: Record<string, unknown>, local: Record<string, unknown>): boolean {
  const same = (a: unknown, b: unknown): boolean => {
    if (a instanceof Date || b instanceof Date) {
      const ta = a instanceof Date ? a.getTime() : a == null ? null : new Date(a as string).getTime()
      const tb = b instanceof Date ? b.getTime() : b == null ? null : new Date(b as string).getTime()
      return ta === tb
    }
    if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b)
    // Fehlt ein Feld lokal ganz (ältere Version), ist es für `null` aus der Cloud dasselbe.
    return (a ?? null) === (b ?? null)
  }
  return Object.entries(updates).some(([key, value]) => !same(value, local[key]))
}

/**
 * Generic pull: fetches remote entities and applies them locally.
 * Implements the 7-step algorithm shared by tasks, projects, and sections.
 */
export async function pullRemoteEntities<TRemote>(
  userId: string,
  descriptor: PullDescriptor<TRemote>,
  pendingChanges: SyncQueueItem[],
  cloudState: CloudEntityState | null,
): Promise<PullResult> {
  const result: PullResult = { created: 0, updated: 0, deleted: 0, conflicts: [], errors: [] }
  const cloudCount = cloudState?.count ?? null

  try {
    // 1. Read last sync timestamp
    const lastSyncValue = await db.getSettingValue<string>(descriptor.settingsKey)
    let sinceDate = lastSyncValue ? new Date(lastSyncValue) : undefined

    /*
      1a. **Vorprüfung** — vor `shouldForceFullSync`, nicht danach: Das verwirft
      `sinceDate`, und dann gäbe es nichts mehr zu vergleichen.

      Antwortet die RPC nicht (`cloudState === null`, alter Server oder
      Netzfehler), läuft alles wie bisher: volle Runde.
    */
    if (cloudState && fullRunDone.has(descriptor.entityName)) {
      if (canSkipPull(descriptor.entityName, cloudState, sinceDate)) return result
    }

    // Optional safety check (e.g. zero local tasks → force full sync).
    // Nur, wenn in der Cloud überhaupt etwas liegt — ein Full-Pull gegen eine
    // leere Tabelle findet nie etwas. (Greift nur noch auf dem Fallback-Pfad;
    // im Normalfall hat die Vorprüfung oben schon abgebrochen.)
    if (sinceDate && descriptor.shouldForceFullSync && (cloudCount === null || cloudCount > 0)) {
      if (await descriptor.shouldForceFullSync(sinceDate)) {
        // Local is empty → reset `since` for a full fetch. No standalone log: the
        // end-of-pull summary covers it, staying silent when the feature is also
        // empty in the cloud (was: a "forcing full sync" line every sync).
        sinceDate = undefined
      }
    }

    // A full sync re-evaluates the entire cloud set via fetchRemote, so any
    // previously-cached "skip" accounting is stale — clear it and let this run
    // repopulate it from scratch. Dasselbe für den Zählstand der Abstimmung:
    // Schritt 5 läuft ohne `sinceDate` gar nicht und kann ihn nicht erneuern.
    if (!sinceDate) {
      reconcileSkipCache.delete(descriptor.entityName)
      lastReconciledCount.delete(descriptor.entityName)
    }

    // 2. Fetch remote records
    const remoteRecords = await descriptor.fetchRemote(userId, sinceDate)

    // 3. Build local lookup maps
    const allLocal = await descriptor.getAllLocal()
    const localById = new Map<string, typeof allLocal[0]>()
    const localByCloudId = new Map<string, typeof allLocal[0]>()
    for (const entity of allLocal) {
      localById.set(entity.id, entity)
      if (entity.cloudId) localByCloudId.set(entity.cloudId, entity)
    }

    // 4. Repair stale synced=false flags (entities corrupted by old code)
    // Der Name der Warteschlange ist der der Dexie-Tabelle — aus `entityName + 's'`
    // gebaut, hieß er für `timeEntry` `timeEntrys` und traf nie (#182).
    const pendingEntityIds = new Set(
      pendingChanges.filter(c => c.table === descriptor.table.name).map(c => c.data?.localId)
    )
    let repaired = 0
    for (const entity of allLocal) {
      if (entity.synced === false && entity.cloudId && !pendingEntityIds.has(entity.id)) {
        await withSyncWrite(() => descriptor.table.update(entity.id, { synced: true }))
        entity.synced = true
        repaired++
      }
    }
    if (repaired > 0) {
      console.log(`[Sync] Repaired ${repaired} ${descriptor.entityName}s with stale synced=false`)
    }

    // 5. ID-set reconciliation (count-gated)
    if (sinceDate) {
      /*
        Nur die LEBENDEN Zeilen zählen.

        `localByCloudId` trägt auch soft-deletete Zeilen — es muss sie tragen,
        denn Schritt 6 und 7 unten erkennen eine gelöschte Zeile an ihrem
        Eintrag hier wieder; ohne ihn legte der Pull sie neu an. Die Cloud-Seite
        des Vergleichs zählt dagegen nur `deleted_at IS NULL`
        (`get_sync_counts`, `getCloud*Ids`), und damit zählten beide Seiten
        Verschiedenes.

        Beobachtet am 07.09.2026: `cloud: 40, local: 139` bei JEDEM Zyklus, weil
        95 der 139 lokalen Zeilen tot waren. Das Tor ging nie auf, der volle
        ID-Fetch lief bei jedem Timer-Tick, Realtime-Event und Fensterfokus.
      */
      let localCloudIdCount = 0
      for (const entity of localByCloudId.values()) {
        if (!entity.deletedAt) localCloudIdCount++
      }
      // Cloud records previously fetched but not materialisable locally (orphans)
      // that are STILL missing locally. Treating them as accounted-for lets the
      // gate converge — an orphan that later resolves drops out of this count, so
      // a genuinely new cloud record still re-triggers reconciliation.
      const skipped = reconcileSkipCache.get(descriptor.entityName)
      const accountedSkipped = skipped
        ? [...skipped].filter(id => !localByCloudId.has(id)).length
        : 0
      /*
        `>`, nicht `!==`.

        Die Abstimmung darunter holt AUSSCHLIESSLICH Cloud-Zeilen nach, die
        lokal fehlen (`missingIds`). Steht lokal mehr als in der Cloud, findet
        sie definitionsgemäß nichts — der ID-Fetch wäre reine Verschwendung.
        Nur `cloud > local` kann überhaupt eine Lücke bedeuten.

        Der umgekehrte Überhang ist ein echter Zustand, kein Fehler: Eine Zeile
        wurde in der Cloud gelöscht, während die Löschung an diesem Gerät
        vorbeilief — sie liegt vor `sinceDate`, also holt Schritt 7 sie nie.
        Mit `!==` hielt genau das die Abstimmung für immer offen.
      */
      /*
        …und nur EINMAL je Zählstand.

        `cloud > local` allein reicht als Bedingung nicht: Eine lokal
        soft-gelöschte Zeile, die in der Cloud noch lebt, fällt aus
        `localCloudIdCount` (oben), aber nicht aus `missingIds` (unten, sie steht
        ja in `localByCloudId`). Die Bedingung bleibt damit für immer wahr,
        während der ID-Fetch nie etwas findet — am 20.09.2026 bei `tags` genau so
        gemessen, jeden Lauf, ohne folgendes `id=in.(…)`.

        Fand ein Durchgang nichts, ist die Sache bei DIESEM Zählstand erledigt.
        Kommt eine Zeile hinzu, ändert sich die Zahl und es wird neu abgestimmt.
      */
      const settledAtCount = lastReconciledCount.get(descriptor.entityName)
      const needsReconciliation =
        cloudCount === null ||
        (cloudCount > localCloudIdCount + accountedSkipped && cloudCount !== settledAtCount)

      if (needsReconciliation) {
        console.log(`[Sync] ${descriptor.entityName} reconciliation triggered (cloud: ${cloudCount ?? '?'}, local: ${localCloudIdCount}, accounted: ${accountedSkipped}) — fetching IDs`)
        const cloudIds = await descriptor.fetchRemoteIds(userId)
        // Drop cached skips no longer present server-side so the accounting can't
        // drift (e.g. an orphan that was later deleted in the cloud).
        if (skipped && skipped.size > 0) {
          const cloudIdSet = new Set(cloudIds)
          for (const id of skipped) if (!cloudIdSet.has(id)) skipped.delete(id)
        }
        const alreadyFetchedIds = new Set(remoteRecords.map(r => descriptor.getRemoteId(r)))
        const missingIds = cloudIds.filter(id => !localByCloudId.has(id) && !alreadyFetchedIds.has(id))

        if (missingIds.length > 0) {
          console.log(`[Sync] ID-set reconciliation: ${missingIds.length} ${descriptor.entityName}s in cloud but not local — fetching`)
          const missing = await descriptor.fetchRemoteByIds(userId, missingIds)
          remoteRecords.push(...missing)
        }
      }

      // Bei diesem Zählstand ist abgestimmt — egal ob der Durchgang gelaufen ist
      // oder die Bedingung ihn erspart hat. Beides heißt: Zu dieser Zahl gibt es
      // nichts mehr zu tun.
      if (cloudCount !== null) lastReconciledCount.set(descriptor.entityName, cloudCount)
    }

    // 6. Process each remote record: validate → match → create or update
    for (const rawRemote of descriptor.orderRemote ? descriptor.orderRemote(remoteRecords) : remoteRecords) {
      const parseResult = descriptor.safeParse(rawRemote)
      if (!parseResult.success) {
        const id = (rawRemote as any).id ?? 'unknown'
        result.errors.push(`Invalid remote ${descriptor.entityName} ${id}: ${parseResult.error.message}`)
        continue
      }
      const remote = parseResult.data

      const remoteId = descriptor.getRemoteId(remote)
      const remoteLocalId = descriptor.getRemoteLocalId(remote)

      // Match by local_id first (stable identity), then fall back to cloudId (legacy)
      const localEntity = (remoteLocalId ? localById.get(remoteLocalId) : null)
        || localByCloudId.get(remoteId)

      if (!localEntity) {
        // Insert new record
        try {
          // Guard against duplicates from concurrent sync runs: re-check Dexie
          // for an entity with this cloudId before inserting.
          const existingByCloudId = await descriptor.table.where('cloudId').equals(remoteId).first()
          if (existingByCloudId) continue

          const insertData = await descriptor.buildLocalInsert(remote, userId)
          if (!insertData) {
            // Skipped intentionally (e.g. parent not yet synced / orphaned).
            // Remember it so the count-gated reconciliation above converges
            // instead of re-fetching this record on every sync.
            rememberSkippedCloudId(descriptor.entityName, remoteId)
            continue
          }
          await withSyncWrite(() => descriptor.table.add(insertData))
          forgetSkippedCloudId(descriptor.entityName, remoteId)
          result.created++
        } catch (error: any) {
          const msg = error?.name === 'SchemaError'
            ? `[SCHEMA] ${descriptor.entityName}: ${error.message} — DB upgrade needed`
            : `Failed to create local ${descriptor.entityName}: ${error}`
          console.error(`[Sync] ${msg}`)
          result.errors.push(msg)
        }
      } else {
        // Update existing record
        const updates: Record<string, any> = {}

        // Backfill cloudId if missing (legacy)
        if (!localEntity.cloudId) {
          updates.cloudId = remoteId
          updates.userId = userId
        }

        // Build entity-specific updates (LWW: remote wins when local is clean and older)
        const remoteUpdated = new Date(descriptor.getRemoteTimestamp(remote))
        const localUpdated = new Date(localEntity.updatedAt).getTime()
        let appliedRemoteUpdate = false
        if (localEntity.synced !== false && remoteUpdated.getTime() >= localUpdated) {
          const entityUpdates = await descriptor.buildLocalUpdate(remote, localEntity)
          /*
            **Gleich alt heißt nicht gleich** (gemeldet 2026-09-26): Eine ältere
            App-Version holt eine Zeile, übersieht eine neue Spalte und übernimmt
            dabei den Zeitstempel der Cloud. Nach dem Update kommt dieselbe Zeile
            wieder — auf die Millisekunde gleich alt. Nur „neuer" zu übernehmen
            verwarf sie, und die Spalte kam nie an. Bei gleichem Stand gilt die
            Cloud darum, wenn sie sich wirklich unterscheidet; sonst bleibt die
            Zeile unberührt (die Abfrage holt ab `since` mit `>=`, die Zeile am
            Merker käme bei jedem Sync wieder).
          */
          const newer = remoteUpdated.getTime() > localUpdated
          if (entityUpdates && (newer || differsFromLocal(entityUpdates, localEntity))) {
            Object.assign(updates, entityUpdates)
            appliedRemoteUpdate = true
          }
        } else if (localEntity.synced === false) {
          console.log(`[Sync] PULL skipped (dirty): ${descriptor.entityName} cloudId=${remoteId}`)
        }

        if (Object.keys(updates).length > 0) {
          await withSyncWrite(() =>
            descriptor.table.update(localEntity.id, { ...updates, synced: true, syncedAt: new Date() })
          )
          if (appliedRemoteUpdate) result.updated++
        }
      }
    }

    // 7. Pull remote deletions
    const deletedRemote = await descriptor.fetchDeletedRemote(userId, sinceDate)
    for (const deleted of deletedRemote) {
      const localEntity = ((deleted as any).local_id ? localById.get((deleted as any).local_id) : null)
        || localByCloudId.get(deleted.id)
      if (localEntity && !localEntity.deletedAt && localEntity.synced !== false) {
        await withSyncWrite(() => descriptor.table.update(localEntity.id, { deletedAt: new Date() }))
        await descriptor.afterRemoteDelete?.(localEntity.id)
        result.deleted++
      }
    }

    /*
      Den **Wasserstand** vorrücken.

      Er kommt vom Server (ADR 0027): das Maximum aus der Vorprüfung, nicht das
      Maximum der geholten Zeilen. Der Grund steht in (C) des Plans —
      `fetchRemote` lässt gelöschte Zeilen aus, deren Zeitstempel käme also nie
      in den Wasserstand, und die Vorprüfung ginge nach einer Löschung nie wieder
      zu. Ein Soft-Delete ist dagegen ein UPDATE und steht im Server-Maximum.

      Sicher ist das, weil die Vorprüfung VOR den Pulls läuft: Ihr Wert ist nie
      neuer als das, was der Pull gesehen hat. Schlimmstenfalls holt der nächste
      Lauf eine Zeile doppelt, nie zu wenig. Aus demselben Grund ist das
      Abschneiden der Mikrosekunden durch `new Date()` unkritisch — es rundet ab.

      Ohne Antwort der Vorprüfung bleibt es beim bisherigen Weg.
    */
    if (result.errors.length === 0) {
      if (cloudState) {
        // `maxUpdatedAt === null` heißt: leere Tabelle. Nichts vorzurücken, und
        // der bestehende Wasserstand bleibt stehen.
        if (cloudState.maxUpdatedAt) {
          await db.setSetting(descriptor.settingsKey, new Date(cloudState.maxUpdatedAt).toISOString())
        }
      } else {
        let maxServerTime = sinceDate?.toISOString() ?? ''
        for (const r of remoteRecords) {
          const ts = descriptor.getRemoteTimestamp(r)
          if (ts > maxServerTime) maxServerTime = ts
        }
        for (const d of deletedRemote) {
          if (d.updated_at > maxServerTime) maxServerTime = d.updated_at
        }
        const newSyncTime = maxServerTime || new Date().toISOString()
        await db.setSetting(descriptor.settingsKey, newSyncTime)
      }

      // Erst jetzt gilt der volle Lauf als getan: Ein Lauf, der mit Fehlern
      // endet, darf den nächsten nicht überspringen lassen.
      fullRunDone.add(descriptor.entityName)
    }

    // Single per-entity summary, only when local data actually changed — keeps
    // the log quiet on the common no-op sync (boundary re-pull / empty feature).
    if (result.created || result.updated || result.deleted) {
      console.log(`[Sync] pull${descriptor.entityName}s: +${result.created} ~${result.updated} -${result.deleted}`)
    }

    return result
  } catch (error: any) {
    const msg = error?.name === 'SchemaError'
      ? `[SCHEMA] Pull ${descriptor.entityName}s failed: ${error.message} — DB upgrade needed`
      : `Pull ${descriptor.entityName}s failed: ${error}`
    console.error(`[Sync] ${msg}`)
    result.errors.push(msg)
    return result
  }
}
